import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../state/auth";
import { useOrder } from "../state/order";
import { useBasketUI } from "../state/basketUi";
import Button from "./Button";

const Navigation: React.FC = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  const { token, logout } = useAuth();
  const { items } = useOrder();
  const { toggleBasket } = useBasketUI();
  const handleLogout = () => {
    logout();
    window.location.href = "/login";
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  console.log("Render Navigation");

  return (
    <nav className="relative bg-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link
              to="/"
              className="text-2xl font-bold text-brand-600 flex items-center"
            >
              <img
                src="/images/logo.png"
                alt="Toddlar"
                className="h-16 w-16 object-contain mr-2"
              />
              <span className="text-2xl font-bold text-brand-600">Toddla</span>
            </Link>
          </div>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-4">
            {token ? (
              <>
                <Link
                  to="/status"
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive("/status")
                      ? "text-brand-600 bg-brand-50"
                      : "text-gray-700 hover:text-brand-600 hover:bg-gray-50"
                  }`}
                >
                  Status
                </Link>
                <Link
                  to="/browse"
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive("/browse")
                      ? "text-brand-600 bg-brand-50"
                      : "text-gray-700 hover:text-brand-600 hover:bg-gray-50"
                  }`}
                >
                  Browse
                </Link>
                <button
                  onClick={toggleBasket}
                  className="relative px-3 py-2 rounded-md text-sm font-medium transition-colors text-gray-700 hover:text-brand-600 hover:bg-gray-50"
                >
                  Basket
                  {items.length > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center rounded-full bg-brand-600 text-white text-xs px-2 py-0.5">
                      {items.length}
                    </span>
                  )}
                </button>
                <Button variant="outline" size="small" onClick={handleLogout}>
                  Logout
                </Button>
              </>
            ) : null}
            {!token ? (
              <>
                <Link to="/login">
                  <Button variant="outline" size="small">
                    Login
                  </Button>
                </Link>
                <Link
                  to="/browse"
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive("/browse")
                      ? "text-brand-600 bg-brand-50"
                      : "text-gray-700 hover:text-brand-600 hover:bg-gray-50"
                  }`}
                >
                  Browse
                </Link>
              </>
            ) : null}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              aria-label="Toggle menu"
              className="inline-flex items-center justify-center rounded-md p-2 text-gray-600 hover:text-brand-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-6 w-6"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 11-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-6 w-6"
                >
                  <path d="M3.75 6.75A.75.75 0 014.5 6h15a.75.75 0 010 1.5h-15a.75.75 0 01-.75-.75zm0 5.25a.75.75 0 01.75-.75h15a.75.75 0 010 1.5h-15a.75.75 0 01-.75-.75zm0 5.25a.75.75 0 01.75-.75h15a.75.75 0 010 1.5h-15a.75.75 0 01-.75-.75z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown panel (no full-screen overlay to avoid heavy repaints) */}
      {mobileOpen && (
        <div className="md:hidden absolute right-4 top-16 z-40 w-64 rounded-lg border border-gray-200 bg-white shadow-xl p-2">
          <div className="flex flex-col divide-y divide-gray-100">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className={`px-4 py-3 text-sm font-medium rounded-md ${
                isActive("/")
                  ? "text-brand-700 bg-brand-50"
                  : "text-gray-700 hover:text-brand-700 hover:bg-gray-50"
              }`}
            >
              Home
            </Link>
            {token ? (
              <>
                <Link
                  to="/status"
                  onClick={() => setMobileOpen(false)}
                  className={`px-4 py-3 text-sm font-medium rounded-md ${
                    isActive("/status")
                      ? "text-brand-700 bg-brand-50"
                      : "text-gray-700 hover:text-brand-700 hover:bg-gray-50"
                  }`}
                >
                  Status
                </Link>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    toggleBasket();
                  }}
                  className="px-4 py-3 text-left text-sm font-medium rounded-md text-gray-700 hover:text-brand-700 hover:bg-gray-50"
                >
                  Basket {items.length > 0 ? `(${items.length})` : ""}
                </button>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleLogout();
                  }}
                  className="m-2 px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="m-2 px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-center"
              >
                Login
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
